import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { LoadingIndicator } from '../LoadingIndicator';
import { PointLabelStyleForm, TemplateForm } from './PointLabelStyleForm';
import { useTemplates } from '../../hooks/api/use-templates.hook';
import {
  PointLabelStyleApiModel,
  PointLabelStyleRelationship,
  WithTemplates,
} from '../../../../types/models/point-label-style.model';
import {
  useCreatePointLabelStyle,
  useUpdatePointLabelStyle,
  useGetPointLabelStyle,
  useAddTemplateToPointLabelStyle,
  useRemoveTemplateFromPointLabelStyle,
} from '../../hooks/api/use-point-label-styles.hook';
import { getDirtyValues } from '../../utils/react-hook-form';

export const AddEditPointLabelStyle = () => {
  const navigate = useNavigate();
  const { pointLabelStyleId } = useParams<{ pointLabelStyleId: string }>();

  const {
    pointLabelStyle,
    loading: loadingPointLabelStyle,
    error: gettingError,
  } = useGetPointLabelStyle<PointLabelStyleApiModel & WithTemplates>({
    pointLabelStyleId: Number(pointLabelStyleId),
    include: [PointLabelStyleRelationship.TEMPLATES],
  });
  const [createPointLabelStyle, { loading: creatingPointLabelStyle, error: createError }] = useCreatePointLabelStyle();
  const [updatePointLabelStyle, { loading: updatingPointLabelStyle, error: updateError }] = useUpdatePointLabelStyle();
  const [addTemplateToPointLabelStyle] = useAddTemplateToPointLabelStyle();
  const [removeTemplateFromPointLabelStyle] = useRemoveTemplateFromPointLabelStyle();
  const { loading: loadingTemplates, response: templatesResponse } = useTemplates({});

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, dirtyFields, isSubmitting },
  } = useForm<PointLabelStyleForm>({
    resolver: classValidatorResolver(PointLabelStyleForm),
    values: pointLabelStyle,
  });

  const onSubmit: SubmitHandler<PointLabelStyleForm> = async (submittedValues) => {
    if (!pointLabelStyle) {
      const { templates, ...values } = submittedValues;
      await createPointLabelStyle({ ...values, templateIds: templates.map(({ id }) => id) });
      return;
    }

    const { templates, ...values } = getDirtyValues(dirtyFields, submittedValues);
    const templatesToAdd =
      templates?.filter(({ id }) => !pointLabelStyle.templates.find((template) => template.id === id)) ?? [];
    const templatesToRemove = templates
      ? pointLabelStyle.templates.filter(({ id }) => !templates?.find((template) => template.id === id))
      : [];

    await Promise.all([
      updatePointLabelStyle({ ...values, pointLabelStyleId: pointLabelStyle.id }),
      ...templatesToAdd.map(({ id: templateId }) =>
        addTemplateToPointLabelStyle({ pointLabelStyleId: pointLabelStyle.id, templateId }),
      ),
      ...templatesToRemove.map(({ id: templateId }) =>
        removeTemplateFromPointLabelStyle({ pointLabelStyleId: pointLabelStyle.id, templateId }),
      ),
    ]);

    navigate('..');
  };

  const renderContent = () => {
    if (gettingError) {
      return <Alert severity="error">{gettingError?.response?.data.message}</Alert>;
    }

    const isDisabled = creatingPointLabelStyle || updatingPointLabelStyle;

    return (
      <div>
        <TextField
          {...register('name')}
          variant="outlined"
          sx={{ mt: '1rem' }}
          disabled={isDisabled}
          error={Boolean(errors.name)}
          helperText={errors.name?.message}
          label="Name"
          margin="dense"
          size="small"
          fullWidth
        />
        <Controller
          control={control}
          name="templates"
          render={({ field }) => (
            <Autocomplete
              {...register('templates')}
              loading={loadingTemplates}
              sx={{ mt: '1rem' }}
              multiple
              onChange={(_event, selectedOptions) => field.onChange([...selectedOptions])}
              onBlur={field.onBlur}
              value={field.value}
              getOptionLabel={(template) => (template as TemplateForm)?.name ?? ''}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              options={templatesResponse?.pages.flatMap(({ result }) => result) ?? []}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Template(s)"
                  error={Boolean(errors.templates)}
                  helperText={errors.templates?.message}
                />
              )}
            />
          )}
        />
      </div>
    );
  };

  if (loadingPointLabelStyle) {
    // FIXME: workaround for react-use-form not resetting controlled fields in a dialog
    return <LoadingIndicator />;
  }

  return (
    <Dialog open={true}>
      {loadingPointLabelStyle ? (
        <LoadingIndicator />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} style={{ width: 600 }}>
          <DialogTitle>{`${pointLabelStyle ? 'Edit' : 'Add'} Civil 3D Point Label Style`}</DialogTitle>
          <DialogContent>
            {renderContent()}
            {createError || updateError ? (
              <Alert sx={{ mt: '1rem' }} severity="error">
                {createError?.response?.data?.message || updateError?.response?.data?.message}
              </Alert>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button disabled={isSubmitting} variant="outlined" onClick={() => navigate('..')} color="primary">
              Cancel
            </Button>
            <LoadingButton
              color="primary"
              type="submit"
              variant="contained"
              loading={isSubmitting || Boolean(gettingError)}
            >
              {pointLabelStyle ? 'Save' : 'Create'}
            </LoadingButton>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};
